import { z } from 'zod';
import {
  JOB_SEARCH_ALGORITHM,
  JOB_START_DATE,
  JOB_TYPE,
} from './job.constants';
import { AddressSchema } from 'lib/consumer/address/address.schema';

export const PreferredStartDateSchema = z.union([
  z.object({
    option: z.union([
      z.literal(JOB_START_DATE.asap),
      z.literal(JOB_START_DATE.flexible),
      z.literal(JOB_START_DATE.inPlanning),
      z.literal(JOB_START_DATE.within1Month),
      z.literal(JOB_START_DATE.within1Week),
      z.literal(JOB_START_DATE.within2Weeks),
      z.literal(JOB_START_DATE.withinFewDays),
      z.literal(JOB_START_DATE.readyToBook),
    ]),
    label: z.string().optional(),
  }),
  z.object({
    option: z.literal(JOB_START_DATE.urgent),
    level: z.literal(48), // 48 Hours
    label: z.string().optional(),
  }),
]);

export const UserProfileSchema = z.object({
  homeownerId: z.string().optional(),
  name: z.string(),
  email: z.string().email(),
  phoneNumber: z.string(),
  address: AddressSchema,
  updateProfile: z.boolean().optional(),
});

export const AdditionalDataItem = z.object({
  question: z.string(),
  answer: z.string(),
  questionId: z.number(),
  answerId: z.number(),
});

const AdditionalDataItemWithShortName = AdditionalDataItem.extend({
  shortName: z.string().nullish(),
});

export const AdditionalDataSchema = z.array(AdditionalDataItemWithShortName);

const JobRefinementFormSchema = z.object({
  formId: z.string(),
  experimentId: z.string().optional(),
  variant: z.string().optional(),
  experimentName: z.string().optional(),
  questions: z.array(
    z.object({
      title: z.string(),
      questionId: z.string(),
      answers: z.array(
        z.object({
          key: z.string().optional(),
          value: z.string(),
        }),
      ),
      format: z.string(),
    }),
  ),
});

export const JobSchema = z.object({
  type: z.nativeEnum(JOB_TYPE),
  categoryId: z.number(),
  description: z.string().min(10).max(500).trim(),
  preferredStart: PreferredStartDateSchema,
  userProfile: UserProfileSchema,
  additionalData: AdditionalDataSchema.optional(),
  leads: z.array(z.number()).optional(),
  mediaAttachmentIds: z.array(z.string()).optional(),
  jobRefinementForm: JobRefinementFormSchema.nullish(),
  creationSource: z.string().optional(),
  serviceId: z.string().nullable().optional(),
  serviceVersionId: z.string().nullable().optional(),
});

export const PartialJobSchema = JobSchema.partial().merge(
  JobSchema.pick({ userProfile: true }).deepPartial(),
);

export const JobRequestSchema = JobSchema.extend({
  variants: z.object({
    search: z.nativeEnum(JOB_SEARCH_ALGORITHM).optional(),
  }),
}).transform(({ userProfile, leads = [], additionalData = [], ...rest }) => ({
  ...rest,
  homeownerId: userProfile.homeownerId,
  consumerName: userProfile.name,
  consumerEmail: userProfile.email,
  address: userProfile.address,
  // We might want to move this .replace to UserProfileSchema
  phoneNumber: userProfile.phoneNumber.replace(/\s/g, ''),
  leads: leads.map(id => ({ tradeId: id })),
  additionalData: additionalData.map(item => ({
    question: item.shortName || item.question,
    answer: item.answer,
  })),
}));
